"use client";

import {
  Button,
  createSortableColumn,
  DataTable,
  Skeleton,
  type TanstackReactTable,
} from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  ClockIcon,
  EyeIcon,
  PercentIcon,
  TrendingUpIcon,
  XIcon,
} from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type {
  FilterConditionDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
} from "@/shared/contracts/query-helpers";
import { formatDateTime, formatDateTimeLocal } from "@/shared/lib/date-time";
import { formatPrice } from "@/shared/lib/format-price";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { RowActions } from "@/shared/ui/RowActions";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { SummaryCard } from "@/shared/ui/SummaryCard";
import type { TopupRequestItem } from "../api/topup-client";
import { useTopupListQuery, useTopupStatsQuery } from "../api/topup-queries";

const STATUS_OPTIONS = [
  { value: "all", label: "ສະຖານະທັງໝົດ" },
  { value: "submitted", label: "ລໍຖ້າອະນຸມັດ" },
  { value: "approved", label: "ອະນຸມັດແລ້ວ" },
  { value: "posted", label: "ສຳເລັດ" },
  { value: "rejected", label: "ປະຕິເສດ" },
] as const;

const METHOD_OPTIONS = [
  { value: "all", label: "ວິທີທັງໝົດ" },
  { value: "transfer", label: "ໂອນເງິນ" },
  { value: "provider", label: "ຜູ້ໃຫ້ບໍລິການ" },
] as const;

const STATUS_STYLE: Record<
  string,
  { dot: string; text: string; label: string }
> = {
  submitted: {
    dot: "bg-primary",
    text: "text-primary",
    label: "ລໍຖ້າອະນຸມັດ",
  },
  approved: {
    dot: "bg-secondary",
    text: "text-secondary",
    label: "ອະນຸມັດແລ້ວ",
  },
  posted: {
    dot: "bg-success",
    text: "text-success",
    label: "ສຳເລັດ",
  },
  rejected: {
    dot: "bg-destructive",
    text: "text-destructive",
    label: "ປະຕິເສດ",
  },
};

const OWNER_TYPE_LABELS: Record<string, string> = {
  customer: "ລູກຄ້າ",
  shop: "ຮ້ານຄ້າ",
  mechanic: "ຊ່າງ",
  platform: "ແພລດຟອມ",
};

const METHOD_LABELS: Record<string, string> = {
  transfer: "ໂອນເງິນ",
  provider: "ຜູ້ໃຫ້ບໍລິການ",
};

function TopupStatsCards() {
  const { data: stats, isLoading } = useTopupStatsQuery();

  if (isLoading) {
    return (
      <div className="mb-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-[100px] rounded-xl" />
        ))}
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div className="mb-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      <SummaryCard
        title="ປະລິມານທຸລະກຳ (24 ຊມ)"
        icon={TrendingUpIcon}
        color="orange"
        items={[{ label: "", value: formatPrice(stats.volume24h, "LAK") }]}
        subtitle="24 ຊົ່ວໂມງທີ່ຜ່ານມາ"
        subtitleClassName="text-muted-foreground"
      />
      <SummaryCard
        title="ຄຳຂໍເກົ່າສຸດ"
        icon={ClockIcon}
        color="secondary"
        items={[
          {
            label: "",
            value: stats.oldestRequestAt
              ? formatDateTimeLocal(stats.oldestRequestAt)
              : "ບໍ່ມີ",
          },
        ]}
        subtitle="ວັນທີລໍຖ້າອະນຸມັດທີ່ເກົ່າສຸດ"
        subtitleClassName="text-muted-foreground"
      />
      <SummaryCard
        title="ອັດຕາອະນຸມັດ"
        icon={PercentIcon}
        color="success"
        items={[{ label: "", value: `${stats.approvalRate}%` }]}
        subtitle="ອະນຸມັດ+ສຳເລັດ / ທັງໝົດທີ່ຕັດສິນແລ້ວ"
        subtitleClassName="text-muted-foreground"
      />
    </div>
  );
}

export function TopupRequestsPage() {
  const nav = useNavigate({ from: "/app/wallet/topup" });
  const search: OffsetPageQueryDTO = useSearch({
    from: "/app/wallet/topup",
  });

  const list = useTopupListQuery({
    offset: search.offset,
    limit: search.limit,
    sort: search.sort,
    filters: search.filters,
  });

  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];
  const statusFilter = findCondition(filters, "status");
  const methodFilter = findCondition(filters, "method");

  const statusValue = (statusFilter?.value as string) || "all";
  const methodValue = (methodFilter?.value as string) || "all";
  const hasFilter = statusValue !== "all" || methodValue !== "all";

  function handleFilterChange(field: string, value: string) {
    let next: FilterConditionDTO[] | undefined = filters;
    if (value === "all") {
      next = removeConditions(filters, field);
    } else {
      next = upsertCondition(filters, { field, op: "eq", value });
    }
    nav({
      search: {
        ...search,
        offset: 0,
        filters: next?.length ? next : undefined,
      },
    });
  }

  const columns: TanstackReactTable.ColumnDef<TopupRequestItem>[] = [
    createSortableColumn<TopupRequestItem>("createdAt", "ວັນທີ", {
      size: 170,
      cell: ({ row }) => (
        <span className="text-sm tabular-nums">
          {formatDateTime(row.original.createdAt)}
        </span>
      ),
    }),
    {
      id: "owner",
      header: "ເຈົ້າຂອງ",
      cell: ({ row }) => {
        const r = row.original;
        return (
          <div className="min-w-0">
            <div className="truncate font-medium text-sm">
              {r.ownerName ?? "—"}
            </div>
            <div className="text-muted-foreground text-xs">
              {OWNER_TYPE_LABELS[r.ownerType] ?? r.ownerType}
            </div>
          </div>
        );
      },
      size: 200,
    },
    {
      id: "amount",
      header: "ຈຳນວນເງິນ",
      cell: ({ row }) => (
        <span className="font-semibold text-success tabular-nums">
          +{formatPrice(row.original.amount, row.original.currency)}
        </span>
      ),
      size: 160,
    },
    {
      id: "method",
      header: "ວິທີ",
      cell: ({ row }) => (
        <span className="text-sm">
          {METHOD_LABELS[row.original.method] ?? row.original.method}
        </span>
      ),
      size: 130,
    },
    {
      id: "status",
      header: "ສະຖານະ",
      cell: ({ row }) => {
        const s = row.original.status;
        const style = STATUS_STYLE[s] ?? {
          dot: "bg-muted-foreground",
          text: "text-muted-foreground",
          label: s,
        };
        return (
          <div className="flex items-center gap-2">
            <span
              className={`size-2 shrink-0 rounded-full ${style.dot}`}
              aria-hidden
            />
            <span className={`text-sm ${style.text}`}>{style.label}</span>
          </div>
        );
      },
      size: 140,
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <RowActions
          actions={[
            {
              label: "ເບິ່ງລາຍລະອຽດ",
              icon: <EyeIcon className="size-4" />,
              onClick: () =>
                nav({
                  to: "/app/wallet/topup/$id",
                  params: { id: row.original.id },
                }),
            },
          ]}
        />
      ),
      size: 60,
    },
  ];

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ກະເປົາເງິນ & ການຈ່າຍເງິນ", to: "/app/wallet" },
            { label: "ຄຳຂໍເຕີມເງິນ" },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-2 flex flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between space-y-2">
            <div>
              <h2 className="font-bold text-2xl tracking-tight">ຄຳຂໍເຕີມເງິນ</h2>
              <p className="text-muted-foreground">
                ກວດສອບ ແລະ ອະນຸມັດການເຕີມເງິນເຂົ້າກະເປົາ
              </p>
            </div>
          </div>
        </div>

        <TopupStatsCards />

        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <div className="mb-4 flex flex-col gap-2 px-2">
            <div className="flex items-center justify-between gap-4 max-sm:flex-col">
              <div className="flex flex-1 flex-wrap items-center gap-2">
                <SimpleSelect
                  value={statusValue}
                  onValueChange={(val) => handleFilterChange("status", val)}
                  placeholder="ສະຖານະ"
                  className="h-8 w-full sm:w-44"
                  options={STATUS_OPTIONS.map((o) => ({
                    value: o.value,
                    label: o.label,
                  }))}
                />
                <SimpleSelect
                  value={methodValue}
                  onValueChange={(val) => handleFilterChange("method", val)}
                  placeholder="ວິທີ"
                  className="h-8 w-full sm:w-40"
                  options={METHOD_OPTIONS.map((o) => ({
                    value: o.value,
                    label: o.label,
                  }))}
                />
              </div>
              {hasFilter && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    nav({
                      search: { ...search, filters: undefined, offset: 0 },
                    })
                  }
                  className="max-sm:w-full"
                >
                  <XIcon className="h-4 w-4" /> ລ້າງ
                </Button>
              )}
            </div>
          </div>

          <DataTable<TopupRequestItem, unknown>
            noDataMessage="ບໍ່ພົບຄຳຂໍເຕີມເງິນ"
            isLoading={list.isLoading}
            columns={columns}
            data={list.data?.data ?? []}
            offset={search.offset ?? 0}
            limit={search.limit ?? 20}
            totalCount={list.data?.meta?.total ?? 0}
            onPaginationChange={(p) =>
              nav({ search: { ...search, offset: p.offset, limit: p.limit } })
            }
            sortBy={search.sort ? search.sort[0]?.field : undefined}
            sortOrder={
              search.sort ? (search.sort[0]?.dir as "asc" | "desc") : undefined
            }
            onSortingChange={(sorting) => {
              if (sorting[0]?.id === "") return;
              nav({
                search: {
                  ...search,
                  sort: [
                    {
                      field: sorting[0]?.id as string,
                      dir: sorting[0]?.desc ? "desc" : "asc",
                    },
                  ],
                },
              });
            }}
          />
        </div>
      </Main>
    </>
  );
}
